import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { TemplateItem, User } from '@/lib/types'
import { apiClient, TokenExpiredError } from '@/lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { storage, storageKeys } from '@/lib/storage'
import { Plus } from 'lucide-react'
import { authService } from '@/lib/auth'

export function LibraryPage() {
  const [tab, setTab] = useState<'personal' | 'public' | 'team'>('personal')
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [query, setQuery] = useState('')
  const [keywords, setKeywords] = useState<string>('')
  const [openCreate, setOpenCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formType, setFormType] = useState<'prompt' | 'persona'>('prompt')
  const [formTitle, setFormTitle] = useState('New Template')
  const [formDesc, setFormDesc] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formVis, setFormVis] = useState<'personal' | 'public'>('personal')
  const [formKeywords, setFormKeywords] = useState('')
  const navigate = useNavigate()
  const { toast } = useToast()
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  // Publish dialog state
  const [openPublish, setOpenPublish] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishId, setPublishId] = useState<number | null>(null)
  const [publishTitle, setPublishTitle] = useState('')
  const [publishDesc, setPublishDesc] = useState('')
  const [publishContent, setPublishContent] = useState('')

  // Use confirmation dialog state
  const [openUse, setOpenUse] = useState(false)
  const [using, setUsing] = useState(false)
  const [useId, setUseId] = useState<number | null>(null)
  const [useTitle, setUseTitle] = useState('')
  const [useDesc, setUseDesc] = useState('')
  const [useContent, setUseContent] = useState('')

  // View dialog state (reuses useTitle, useDesc, useContent)
  const [openView, setOpenView] = useState(false)

  // Fork confirmation dialog state
  const [openFork, setOpenFork] = useState(false)
  const [forking, setForking] = useState(false)
  const [forkId, setForkId] = useState<number | null>(null)
  const [forkTitle, setForkTitle] = useState('')

  const load = async () => {
    try {
      const res = await apiClient.listTemplates(tab === 'team' ? 'public' : tab, query || '', 1, 20, keywords || undefined)
      setTemplates(res.items)
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await authService.logout()
        navigate('/login?expired=true', { replace: true })
        return
      }
      throw error // Re-throw other errors
    }
  }

  const resetForm = () => {
    setIsEdit(false)
    setEditingId(null)
    setFormType('prompt')
    setFormTitle('New Template')
    setFormDesc('')
    setFormContent('')
    setFormVis('personal')
    setFormKeywords('')
  }

  useEffect(() => { load() }, [tab])
  useEffect(() => {
    (async () => {
      try {
        const me = await apiClient.getCurrentUser()
        setCurrentUser(me)
      } catch (error) {
        // Handle token expiration - redirect to login page
        if (error instanceof TokenExpiredError) {
          await authService.logout()
          navigate('/login?expired=true', { replace: true })
          return
        }
        // Silently ignore other errors for user loading
      }
    })()
  }, [navigate])

  const handleCreate = async () => {
    try {
      setCreating(true)
      if (isEdit && editingId) {
        const updated = await apiClient.updateTemplate(editingId, {
          title: formTitle.substring(0, 50),
          description: formDesc.substring(0, 100) || undefined,
          content: formContent.substring(0, 20000),
          keywords: formKeywords
            ? formKeywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean).slice(0, 10)
            : undefined,
        })
        setTemplates(prev => prev.map(t => t.id === editingId ? updated : t))
      } else {
        await apiClient.createTemplate({
          type: formType,
          title: formTitle.substring(0, 50),
          description: formDesc.substring(0, 100) || undefined,
          content: formContent.substring(0, 20000),
          visibility: formVis,
          keywords: formKeywords
            ? formKeywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean).slice(0, 10)
            : undefined,
        })
      }
      setOpenCreate(false)
      resetForm()
      await load()
      if (!isEdit && tab !== formVis) setTab(formVis)
    } catch (e: any) {
      // Handle token expiration - redirect to login page
      if (e instanceof TokenExpiredError) {
        await authService.logout()
        navigate('/login?expired=true', { replace: true })
        return
      }
      
      // Basic feedback if request fails
      const errorMsg = e?.message || (isEdit ? 'Failed to update template' : 'Failed to create template')
      if (errorMsg.includes('already exists')) {
        toast({
          title: "Template already exists",
          description: `Template "${formTitle}" already exists in public templates. Please use a different title.`,
          variant: "destructive",
        })
      } else {
        toast({
          title: isEdit ? "Failed to update template" : "Failed to create template",
          description: errorMsg,
          variant: "destructive",
        })
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-semibold">Template Library</h1>
          <div className="space-x-2">
            <Button variant="outline" onClick={() => navigate('/')}>Back to Chat</Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Check out templates to quickly start new chats or share your best prompts.</p>

        <div className="flex items-center space-x-2 mb-4">
          <Button variant={tab === 'personal' ? 'default' : 'outline'} onClick={() => setTab('personal')}>Personal</Button>
          <Button variant={tab === 'public' ? 'default' : 'outline'} onClick={() => setTab('public')}>Public</Button>
          <Button variant={tab === 'team' ? 'default' : 'outline'} onClick={() => setTab('team')}>Team (Coming later)</Button>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          <input 
            className="border rounded px-3 py-2 w-64 bg-background text-foreground" 
            placeholder="Search..." 
            value={query} 
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                load()
              }
            }}
          />
          <input 
            className="border rounded px-3 py-2 w-64 bg-background text-foreground" 
            placeholder="Search keywords" 
            value={keywords} 
            onChange={(e) => setKeywords(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                load()
              }
            }}
          />
          <Button onClick={load}>Search</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tab === 'team' ? (
            <div className="p-6 border rounded text-muted-foreground">Team area is coming later.</div>
          ) : (
            <>
              {tab !== 'team' && (
                <div 
                  onClick={() => {
                    resetForm()
                    setOpenCreate(true)
                  }}
                  className="border rounded p-4 bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center justify-center min-h-[180px]"
                >
                  <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                    <Plus className="w-12 h-12 mb-2" />
                    <span className="text-sm font-medium">Create Template</span>
                  </div>
                </div>
              )}
              {templates.map(t => (
              <div key={t.id} className="border rounded p-4 bg-white dark:bg-gray-900">
                <div className="text-sm text-muted-foreground mb-1">{t.type.toUpperCase()} • {t.visibility}</div>
                <div className="font-medium">{t.title}</div>
                {t.description && <div className="text-sm text-muted-foreground mt-1">{t.description}</div>}
                {t.keywords && t.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.keywords.map((kw, idx) => (
                      <span key={idx} className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                        {kw}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-2">
                  By {t.owner_display || 'Unknown'}{t.forked_from_id ? ' • Forked from original' : ''}
                  <br />
                  Created: {new Date(t.created_at).toLocaleString()}
                </div>
                <div className="flex items-center space-x-3 mt-3">
                  <Button size="sm" variant="outline" onClick={() => {
                    setUseTitle(t.title)
                    setUseDesc(t.description || '')
                    setUseContent(t.content)
                    setOpenView(true)
                  }}>View</Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    setUseId(t.id)
                    setUseTitle(t.title)
                    setUseDesc(t.description || '')
                    setUseContent(t.content)
                    setOpenUse(true)
                  }}>Use</Button>
                  {tab === 'public' ? (
                    <>
                      {currentUser && t.owner_user_id === currentUser.id ? (
                        <>
                          <Button size="sm" variant="link" className="px-0" onClick={() => {
                            setIsEdit(true)
                            setEditingId(t.id)
                            setFormType(t.type)
                            setFormTitle(t.title)
                            setFormDesc(t.description || '')
                            setFormContent(t.content)
                            setFormVis(t.visibility)
                            setFormKeywords((t.keywords || []).join(','))
                            setOpenCreate(true)
                          }}>Edit</Button>
                          <Button size="sm" variant="link" className="px-0" onClick={async () => { 
                            if (confirm('Are you sure you want to delete this template?')) { 
                              try {
                                await apiClient.deleteTemplate(t.id)
                                await load()
                              } catch (e: any) {
                                if (e instanceof TokenExpiredError) {
                                  await authService.logout()
                                  navigate('/login?expired=true', { replace: true })
                                  return
                                }
                                alert(e?.message || 'Failed to delete template')
                              }
                            }
                          }}>Delete</Button>
                        </>
                      ) : null}
                      <Button size="sm" variant="link" className="px-0" onClick={() => {
                        setForkId(t.id)
                        setForkTitle(t.title)
                        setOpenFork(true)
                      }}>Fork to Personal</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="link" className="px-0" onClick={() => {
                        setPublishId(t.id)
                        setPublishTitle(t.title)
                        setPublishDesc(t.description || '')
                        setPublishContent(t.content)
                        setOpenPublish(true)
                      }}>Create Public Copy</Button>
                      <Button size="sm" variant="link" className="px-0" onClick={() => {
                        setIsEdit(true)
                        setEditingId(t.id)
                        setFormType(t.type)
                        setFormTitle(t.title)
                        setFormDesc(t.description || '')
                        setFormContent(t.content)
                        setFormVis(t.visibility)
                        setFormKeywords((t.keywords || []).join(','))
                        setOpenCreate(true)
                      }}>Edit</Button>
                      <Button size="sm" variant="link" className="px-0" onClick={async () => { 
                        if (confirm('Are you sure you want to delete this template?')) { 
                          try {
                            await apiClient.deleteTemplate(t.id)
                            await load()
                          } catch (e: any) {
                            if (e instanceof TokenExpiredError) {
                              await authService.logout()
                              navigate('/login?expired=true', { replace: true })
                              return
                            }
                            alert(e?.message || 'Failed to delete template')
                          }
                        }
                      }}>Delete</Button>
                    </>
                  )}
                </div>
              </div>
              ))}
            </>
          )}
        </div>
      </div>
      <Dialog open={openCreate} onOpenChange={(open) => {
        setOpenCreate(open)
        if (!open) {
          resetForm()
        }
      }}>
        <DialogContent onOpenChange={setOpenCreate} className="sm:max-w-3xl w-[92vw] max-w-3xl max-h-[85vh] overflow-auto p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Type</label>
              <select className="border rounded px-2 py-1 bg-background text-foreground" value={formType} onChange={(e) => setFormType(e.target.value as any)} disabled={isEdit}>
                <option value="prompt">prompt</option>
                <option value="persona">persona</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Title</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} maxLength={50} placeholder="Concise name (max 50)" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Description</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} maxLength={100} placeholder={formType === 'prompt' ? 'Description is required for prompt' : 'Optional: briefly describe this template'} />
            </div>
            <div className="flex items-start gap-2">
              <label className="text-sm w-28 mt-2">Content</label>
              <textarea className="border rounded px-2 py-2 flex-1 h-48 bg-background text-foreground" value={formContent} onChange={(e) => setFormContent(e.target.value)} maxLength={20000} placeholder="Write your prompt/persona content here..." />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Visibility</label>
              <select className="border rounded px-2 py-1 bg-background text-foreground" value={formVis} onChange={(e) => setFormVis(e.target.value as any)}>
                <option value="personal">personal</option>
                <option value="public">public</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Keywords</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" placeholder="comma-separated (max 10)" value={formKeywords} onChange={(e) => setFormKeywords(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !formTitle.trim() || !formContent.trim() || (formType === 'prompt' && !formDesc.trim())}>{isEdit ? 'Save Changes' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish to public with optional micro edits */}
      <Dialog open={openPublish} onOpenChange={setOpenPublish}>
        <DialogContent onOpenChange={setOpenPublish} className="sm:max-w-2xl w-[92vw] p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>Publish to Public (Create Copy)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Title</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={publishTitle} onChange={(e) => setPublishTitle(e.target.value)} maxLength={50} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm w-28">Description</label>
              <input className="border rounded px-2 py-1 flex-1 bg-background text-foreground" value={publishDesc} onChange={(e) => setPublishDesc(e.target.value)} maxLength={100} />
            </div>
            <div className="flex items-start gap-2">
              <label className="text-sm w-28 mt-2">Content</label>
              <textarea className="border rounded px-2 py-2 flex-1 h-40 bg-background text-foreground" value={publishContent} onChange={(e) => setPublishContent(e.target.value)} maxLength={20000} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPublish(false)} disabled={publishing}>Cancel</Button>
            <Button onClick={async () => {
              if (!publishId) return
              try {
                setPublishing(true)
                await apiClient.publishTemplate(publishId, {
                  title: publishTitle.substring(0, 50).trim(),
                  description: publishDesc.substring(0, 100) || undefined,
                  content: publishContent.substring(0, 20000)
                })
                setOpenPublish(false)
                await load()
                setTab('public')
                toast({
                  title: "Template published",
                  description: `"${publishTitle}" has been published to public templates.`,
                })
              } catch (e: any) {
                // Handle token expiration - redirect to login page
                if (e instanceof TokenExpiredError) {
                  await authService.logout()
                  navigate('/login?expired=true', { replace: true })
                  return
                }
                
                console.error('Publish template error:', e)
                const errorMsg = e?.message || 'Failed to publish template'
                // Check for duplicate title error message
                if (errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('duplicate')) {
                  toast({
                    title: "Template already exists",
                    description: errorMsg.includes('already exists') ? errorMsg : `Template "${publishTitle}" already exists in public templates. Please use a different title.`,
                    variant: "destructive",
                  })
                } else {
                  toast({
                    title: "Failed to publish template",
                    description: errorMsg,
                    variant: "destructive",
                  })
                }
              } finally {
                setPublishing(false)
              }
            }} disabled={publishing || !publishTitle.trim() || !publishContent.trim()}>Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog (read-only) */}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent onOpenChange={setOpenView} className="sm:max-w-2xl w-[92vw] p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>View Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-muted-foreground">Title</div>
              <div className="font-medium">{useTitle}</div>
            </div>
            {useDesc && (
              <div>
                <div className="text-sm text-muted-foreground">Description</div>
                <div className="text-sm">{useDesc}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Content</div>
              <div className="border rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap text-sm bg-white dark:bg-gray-900">
                {useContent}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenView(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use confirmation dialog */}
      <Dialog open={openUse} onOpenChange={setOpenUse}>
        <DialogContent onOpenChange={setOpenUse} className="sm:max-w-2xl w-[92vw] p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>Use Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-muted-foreground">Title</div>
              <div className="font-medium">{useTitle}</div>
            </div>
            {useDesc && (
              <div>
                <div className="text-sm text-muted-foreground">Description</div>
                <div className="text-sm">{useDesc}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Content</div>
              <div className="border rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap text-sm bg-white dark:bg-gray-900">
                {useContent}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUse(false)} disabled={using}>Cancel</Button>
            <Button onClick={async () => {
              if (!useId) return
              try {
                setUsing(true)
                // Save template content as DRAFT_MESSAGE for prefill in input box
                storage.setItem(storageKeys.DRAFT_MESSAGE, { content: useContent, title: useTitle })
                await apiClient.markTemplateUsed(useId)
                setOpenUse(false)
                navigate('/')
              } catch (e: any) {
                // Handle token expiration - redirect to login page
                if (e instanceof TokenExpiredError) {
                  await authService.logout()
                  navigate('/login?expired=true', { replace: true })
                  return
                }
                
                alert(e?.message || 'Failed to use template')
              } finally {
                setUsing(false)
              }
            }} disabled={using}>Use</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fork confirmation dialog */}
      <Dialog open={openFork} onOpenChange={setOpenFork}>
        <DialogContent onOpenChange={setOpenFork} className="sm:max-w-md w-[92vw] p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>Fork Template to Personal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to fork "{forkTitle}" to your personal templates? This will create a copy in your personal library.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenFork(false)} disabled={forking}>Cancel</Button>
            <Button onClick={async () => {
              if (!forkId) return
              try {
                setForking(true)
                await apiClient.forkTemplate(forkId)
                setOpenFork(false)
                await load()
                toast({
                  title: "Template forked",
                  description: `"${forkTitle}" has been added to your personal templates.`,
                })
              } catch (e: any) {
                // Handle token expiration - redirect to login page
                if (e instanceof TokenExpiredError) {
                  await authService.logout()
                  navigate('/login?expired=true', { replace: true })
                  return
                }
                
                alert(e?.message || 'Failed to fork template')
              } finally {
                setForking(false)
              }
            }} disabled={forking}>Fork</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}


